import React, { useState, useCallback, useEffect } from 'react';
import { makeStyles, useTheme, Button, Typography, Tooltip, IconButton } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { DATA_ENDPOINT } from '../../../../config';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';

const useStyle = makeStyles((theme) => ({
  job_card_container: {
    border: '1px solid #3b3b3b',
    padding: '10px',
    margin: '10px',
    width: '320px',
    minWidth: 'fit-content',
    minHeight: '170px',
    borderRadius: '5px',
    boxShadow: '0px 0px 4px 0px rgb(0 0 0 / 78%)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },

  task_container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '6px',
    marginBottom: '3%',
  },

  task_info_container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: '6px',
  },

  text_info: {
    fontSize: theme.fonts.responsiveMediumBig,
    color: 'white',
    textTransform: 'uppercase',
  },

  delete_job_container: {
    position: 'absolute',
    top: '2px',
    right: '2px',
  },

  download_button: {
    background: theme.palette.accents.dark,
    color: 'white',
    minWidth: '200px',
    '&:hover': {
      background: theme.palette.accents.main,
      boxShadow: '0px 0px 5px 0px rgb(180 180 180 / 78%)',
    },
  },

  download_button_container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '10px',
    width: '100%',
  },

  empty_task: {
    maxWidth: '220px',
  },

  text_info_empty: {
    fontStyle: 'italic',
    fontSize: theme.fonts.responsiveMedium,
    color: 'white',
    whiteSpace: 'pre-line',
    width: '100%',
    lineHeight: '1.5',
    marginTop: '10px',
  },
}));

const convertTime = (time) => {
  if (!time || time < 60) {
    return '< 1m';
  }
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);

  let result = '';
  if (hours > 0) {
    result += `${hours}h `;
  }
  if (minutes > 0) {
    result += `${minutes}m `;
  }
  return result.trim();
};

const JobItem = ({ job, t, classes, onJobDelete }) => {
  return (
    <div className={classes.job_card_container}>
      <div className={classes.delete_job_container}>
        <Tooltip title={`${t('export.delete')}`}>
          <IconButton aria-label='delete' size='medium' onClick={() => onJobDelete(job?.task_id)}>
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </div>

      {job?.xls?.status === 'skipped' &&
      (job?.operator_report?.status === 'skipped' || job?.csv?.status === 'skipped') &&
      job?.zip?.status === 'skipped' ? (
        <div className={classes.empty_task}>
          <Typography className={classes.text_info} style={{ fontWeight: 'bold' }}>
            {t('export.empty_skipped')}
          </Typography>

          <Typography className={classes.text_info_empty}>{t('export.change_filter')}</Typography>
        </div>
      ) : (
        <>
          {'xls' in job && job?.xls?.status !== 'skipped' && (
            <div className={classes.task_container}>
              <div className={classes.task_info_container}>
                <Typography className={classes.text_info} style={{ fontWeight: 'bold' }}>
                  {t('export.spreadsheet')}
                </Typography>
                <Typography className={classes.text_info}>{`${t('export.status')}  ${
                  job?.xls?.status
                }`}</Typography>
                {job?.xls?.status !== 'processing' && (
                  <Typography className={classes.text_info}>{`${t('export.expires_in')} ${convertTime(
                    job?.xls.expires_in
                  )}`}</Typography>
                )}
              </div>

              {job?.xls?.status === 'completed' && (
                <div className={classes.download_button_container}>
                  <a
                    href={DATA_ENDPOINT + job?.xls.download_url}
                    download
                    target='_blank'
                    rel='noopener noreferrer'
                    className={classes.download_link}>
                    <Button
                      variant='contained'
                      className={classes.download_button}
                      size='small'
                      startIcon={<GetAppIcon />}>
                      {t('export.download_sheet')}
                    </Button>
                  </a>
                </div>
              )}
            </div>
          )}
          {'zip' in job && job?.zip?.status !== 'skipped' && (
            <div className={classes.task_container}>
              <Typography className={classes.text_info} style={{ fontWeight: 'bold' }}>
                {t('export.data')}
              </Typography>
              <Typography className={classes.text_info}>{`${t('export.status')}  ${
                job?.zip?.status
              }`}</Typography>
              {job?.zip?.status !== 'processing' && (
                <Typography className={classes.text_info}>{`${t('export.expires_in')} ${convertTime(
                  job?.zip.expires_in
                )}`}</Typography>
              )}
              {job?.zip?.status === 'completed' && (
                <div className={classes.download_button_container}>
                  <a
                    href={DATA_ENDPOINT + job?.zip.download_url}
                    download
                    target='_blank'
                    rel='noopener noreferrer'
                    className={classes.download_link}>
                    <Button
                      variant='contained'
                      className={classes.download_button}
                      size='small'
                      startIcon={<GetAppIcon />}>
                      {t('export.download_data')}
                    </Button>
                  </a>
                </div>
              )}
            </div>
          )}
          {'operator_report' in job && job?.operator_report?.status !== 'skipped' && (
            <div className={classes.task_container}>
              <Typography className={classes.text_info} style={{ fontWeight: 'bold' }}>
                {t('export.quality_points')}
              </Typography>
              <Typography className={classes.text_info}>{`${t('export.status')}  ${
                job?.operator_report?.status
              }`}</Typography>
              <Typography className={classes.text_info}>{`${t('export.expires_in')} ${convertTime(
                job?.operator_report.expires_in
              )}`}</Typography>
              {job?.operator_report?.status === 'completed' && (
                <div className={classes.download_button_container}>
                  <a
                    href={DATA_ENDPOINT + job?.operator_report.download_url}
                    download
                    target='_blank'
                    rel='noopener noreferrer'
                    className={classes.download_link}>
                    <Button
                      variant='contained'
                      className={classes.download_button}
                      size='small'
                      startIcon={<GetAppIcon />}>
                      {t('export.download_quality_points')}
                    </Button>
                  </a>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ExportData = ({ dataStatus, onJobDelete }) => {
  const classes = useStyle();
  const { t } = useTranslation();
  const theme = useTheme();

  if (!Array.isArray(dataStatus)) {
    return <>{t('export.error')}</>;
  }
  if (dataStatus.length === 0) {
    return null;
  }

  return dataStatus.map((job) => (
    <JobItem key={job?.task_id} job={job} t={t} classes={classes} onJobDelete={onJobDelete} />
  ));
};

export default ExportData;
